import { Mail, Phone, MapPin } from "lucide-react";
import BrandLogo from "./BrandLogo.jsx";
import { scrollToId, FadeUp } from "./Reveal.jsx";
import { useSiteContent } from "@/content/ContentContext.jsx";

const LINKS = [
  { id: "about", label: "About" },
  { id: "facilities", label: "Facilities" },
  { id: "gallery", label: "Gallery" },
  { id: "booking", label: "Book Your Stay" },
  { id: "location", label: "Location & Contact" },
];

const Footer = () => {
  const { content } = useSiteContent();
  const c = content.location;
  const tamil = content.hero?.tamil;

  return (
    <footer className="relative bg-brand-dark text-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <div className="flex items-center gap-4" data-testid="footer-brand">
                <span className="flex items-center justify-center">
                  <BrandLogo size={56} rounded="rounded-2xl" fallbackBg="bg-white/10" testid="footer-logo" />
                </span>
                <div className="leading-tight">
                  <p className="font-display text-lg font-semibold tracking-[0.06em]">ANGUKUMARAN FARMHOUSE</p>
                  <p lang="ta" className="mt-1 font-tamil text-sm text-brand-light" data-testid="footer-tamil-name">
                    {tamil}
                  </p>
                </div>
              </div>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/65">
                {content.footer?.tagline}
              </p>
            </FadeUp>
          </div>

          <div className="lg:col-span-3">
            <FadeUp delay={0.08}>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-light">Explore</h3>
              <ul className="mt-5 space-y-3">
                {LINKS.map((l) => (
                  <li key={l.id}>
                    <button
                      onClick={() => scrollToId(l.id)}
                      className="text-sm text-white/75 transition-colors hover:text-white"
                      data-testid={`footer-link-${l.id}`}
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>

          <div className="lg:col-span-4">
            <FadeUp delay={0.14}>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-light">Contact</h3>
              <ul className="mt-5 space-y-4 text-sm text-white/75">
                <li className="flex items-start gap-3" data-testid="footer-address">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-brand-light" />
                  <span className="whitespace-pre-line">{c.address}</span>
                </li>
                <li className="flex items-center gap-3" data-testid="footer-phone">
                  <Phone size={16} className="shrink-0 text-brand-light" />
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-white">{c.phone}</a>
                </li>
                <li className="flex items-center gap-3" data-testid="footer-email-1">
                  <Mail size={16} className="shrink-0 text-brand-light" />
                  <a href={`mailto:${c.email1}`} className="break-all transition-colors hover:text-white">{c.email1}</a>
                </li>
                <li className="flex items-center gap-3" data-testid="footer-email-2">
                  <Mail size={16} className="shrink-0 text-brand-light" />
                  <a href={`mailto:${c.email2}`} className="break-all transition-colors hover:text-white">{c.email2}</a>
                </li>
              </ul>
            </FadeUp>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-white/50" data-testid="footer-copyright">
            © {new Date().getFullYear()} Angukumaran Farmhouse. All rights reserved.
          </p>
          <p className="text-xs tracking-[0.14em] text-white/50" data-testid="footer-domain">
            {content.footer?.domain}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
